import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { 
  MessageSquare, Wrench, ShoppingBag, Zap, HelpCircle, 
  Plus, Clock, CheckCircle, Loader, ArrowLeft, X, ChevronDown, ChevronUp,
  ExternalLink, Download, Link, AlertCircle, Monitor
} from 'lucide-react';
import { showDiscordRequired } from './DiscordRequiredModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'sav', label: 'SAV / Problème après optimisation', icon: Wrench, color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/30' },
  { id: 'commande', label: 'Question sur une commande', icon: ShoppingBag, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/30' },
  { id: 'technique', label: 'Problème technique', icon: Zap, color: 'text-cyan-400', bg: 'bg-cyan-500/10 border-cyan-500/30' },
  { id: 'autre', label: 'Autre question', icon: HelpCircle, color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/30' },
];

const STATUS_CONFIG = {
  open:        { label: 'Ouvert',     color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/30', dot: 'bg-yellow-400' },
  in_progress: { label: 'En cours',   color: 'text-cyan-400',   bg: 'bg-cyan-500/10 border-cyan-500/30',     dot: 'bg-cyan-400 animate-pulse' },
  closed:      { label: 'Résolu',     color: 'text-green-400',  bg: 'bg-green-500/10 border-green-500/30',   dot: 'bg-green-400' },
};

const SupportPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();

  const [showForm, setShowForm] = useState(false);
  const [myTickets, setMyTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [expandedTicket, setExpandedTicket] = useState(null);

  const [form, setForm] = useState({ category: '', subject: '', description: '', userdiag_link: '' });
  const [userdiagDone, setUserdiagDone] = useState(false);

  const fetchMyTickets = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const res = await fetch(`${API_URL}/api/support/tickets/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setMyTickets(await res.json());
    } catch (e) {}
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchMyTickets(); }, [fetchMyTickets]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.category) { setError('Choisis une catégorie.'); return; }
    if (!form.subject.trim()) { setError('Indique un sujet.'); return; }
    if (!form.description.trim() || form.description.length < 20) { setError('Description trop courte (20 caractères min).'); return; }
    if (!form.userdiag_link.trim()) { setError('Le lien UserDiag est obligatoire. Suis les étapes ci-dessus.'); return; }
    if (!form.userdiag_link.includes('userdiag')) { setError('Le lien doit provenir de userdiag.com.'); return; }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/support/tickets`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 403 && (data.detail === 'not_in_discord' || data.detail === 'discord_banned')) {
          showDiscordRequired(data.detail);
          return;
        }
        setError(data.detail || 'Erreur lors de la création.'); return;
      }
      setSuccessMsg(`Ticket #${data.ticket.ticket_number} créé ! Un canal Discord a été ouvert pour toi.`);
      setForm({ category: '', subject: '', description: '', userdiag_link: '' });
      setUserdiagDone(false);
      setShowForm(false);
      fetchMyTickets();
    } catch (e) {
      setError('Erreur réseau.');
    } finally {
      setSubmitting(false);
    }
  };

  const hasOpenTicket = myTickets.some(t => t.status === 'open' || t.status === 'in_progress');
  const catMap = Object.fromEntries(CATEGORIES.map(c => [c.id, c]));

  if (!isAuthenticated) return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="text-center">
        <MessageSquare className="w-12 h-12 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400 mb-4">Connecte-toi avec Discord pour accéder au support.</p>
        <button onClick={() => window.location.href = `${API_URL}/api/auth/discord`}
          className="px-6 py-2.5 bg-[#5865F2] hover:bg-[#4752C4] text-white rounded-lg transition-colors">
          Se connecter
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen py-8 px-4 md:px-8">
      <div className="max-w-3xl mx-auto">

        {/* Back */}
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 mb-6 transition-colors text-sm">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>

        {/* Header */}
        <div className="flex items-start justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
              <MessageSquare className="w-7 h-7 text-cyan-400" />
              Support AqmOpti
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              Un problème ? On te répond via Discord dans les plus brefs délais.
            </p>
          </div>
          {!showForm && !hasOpenTicket && (
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold text-sm rounded-xl transition-colors whitespace-nowrap"
              data-testid="new-ticket-btn"
            >
              <Plus className="w-4 h-4" /> Nouveau ticket
            </button>
          )}
        </div>

        {/* Success banner */}
        {successMsg && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
              <p className="text-green-300 text-sm">{successMsg}</p>
            </div>
            <button onClick={() => setSuccessMsg('')} className="text-gray-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Warning: already has open ticket */}
        {hasOpenTicket && !showForm && (
          <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex items-center gap-3">
            <Clock className="w-5 h-5 text-yellow-400 shrink-0" />
            <p className="text-yellow-300 text-sm">Tu as déjà un ticket ouvert. Attends qu'il soit résolu pour en ouvrir un nouveau.</p>
          </div>
        )}

        {/* New ticket form */}
        {showForm && (
          <div className="mb-8 bg-zinc-900/60 border border-cyan-900/30 rounded-2xl p-6 animate-fadeIn">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-white font-bold text-lg">Nouveau ticket</h2>
              <button onClick={() => { setShowForm(false); setError(''); }} className="text-gray-400 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Category */}
              <div>
                <label className="block text-sm text-gray-400 font-medium mb-3">Catégorie *</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {CATEGORIES.map(cat => {
                    const Icon = cat.icon;
                    return (
                      <button type="button" key={cat.id}
                        onClick={() => setForm(p => ({ ...p, category: cat.id }))}
                        data-testid={`ticket-cat-${cat.id}`}
                        className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                          form.category === cat.id
                            ? cat.bg + ' ring-1 ring-offset-1 ring-offset-zinc-900 ring-cyan-500'
                            : 'border-zinc-700 bg-zinc-900/40 hover:border-zinc-500'
                        }`}
                      >
                        <Icon className={`w-4 h-4 shrink-0 ${form.category === cat.id ? cat.color : 'text-gray-400'}`} />
                        <span className={`text-sm font-medium ${form.category === cat.id ? 'text-white' : 'text-gray-400'}`}>{cat.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm text-gray-400 font-medium mb-1.5">Sujet *</label>
                <input type="text" value={form.subject}
                  onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                  placeholder="Ex: Mon PC lag après l'optimisation"
                  maxLength={200}
                  className="w-full bg-zinc-950 border border-zinc-700 focus:border-cyan-500 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500 outline-none transition-colors"
                  data-testid="ticket-subject" />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm text-gray-400 font-medium mb-1.5">Description * <span className="text-gray-600">(20 min · 2000 max)</span></label>
                <textarea value={form.description}
                  onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Décris ton problème en détail : que se passe-t-il ? depuis quand ? as-tu déjà essayé quelque chose ?"
                  rows={5} maxLength={2000}
                  className="w-full bg-zinc-950 border border-zinc-700 focus:border-cyan-500 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-500 outline-none transition-colors resize-none"
                  data-testid="ticket-description" />
                <p className="text-right text-xs text-gray-600 mt-1">{form.description.length}/2000</p>
              </div>

              {/* UserDiag Block */}
              <div className="rounded-2xl overflow-hidden border border-cyan-800/40">
                {/* Header */}
                <div className="bg-cyan-950/60 px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Monitor className="w-4 h-4 text-cyan-400" />
                    <span className="text-white font-semibold text-sm">Diagnostic PC obligatoire</span>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 bg-red-500/20 text-red-400 border border-red-500/30 rounded-full font-medium">Requis</span>
                </div>

                {/* Steps */}
                <div className="bg-zinc-900/40 px-4 py-4 space-y-3">
                  <p className="text-gray-300 text-xs">Fais un diagnostic pour qu'on puisse vérifier ton PC avant de traiter ton ticket.</p>
                  
                  <div className="space-y-2.5">
                    {[
                      { num: '1', icon: Download, text: 'Télécharge le logiciel', link: 'https://userdiag.com/', linkText: 'userdiag.com' },
                      { num: '2', icon: X,        text: 'Ferme un maximum d\'applications ouvertes', link: null },
                      { num: '3', icon: Zap,       text: 'Lance le logiciel → clique sur "Diagnostic rapide" (~5 min)', link: null },
                      { num: '4', icon: Link,      text: 'Une fois terminé, copie le lien du résultat et colle-le ci-dessous', link: null },
                    ].map(step => {
                      const StepIcon = step.icon;
                      return (
                        <div key={step.num} className="flex items-start gap-3">
                          <span className="shrink-0 w-5 h-5 rounded-full bg-cyan-600/30 border border-cyan-500/40 text-cyan-400 text-[10px] font-bold flex items-center justify-center mt-0.5">
                            {step.num}
                          </span>
                          <p className="text-gray-300 text-xs leading-relaxed">
                            {step.text}
                            {step.link && (
                              <a href={step.link} target="_blank" rel="noreferrer"
                                className="ml-1.5 inline-flex items-center gap-1 text-cyan-400 hover:text-cyan-300 underline transition-colors">
                                {step.linkText} <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </p>
                        </div>
                      );
                    })}
                  </div>

                  {/* Userdiag link input */}
                  <div className="mt-3 pt-3 border-t border-cyan-900/30">
                    <label className="block text-xs text-cyan-300 font-medium mb-1.5 flex items-center gap-1.5">
                      <Link className="w-3.5 h-3.5" />
                      Lien de ton résultat UserDiag *
                    </label>
                    <div className="relative">
                      <input
                        type="url"
                        value={form.userdiag_link}
                        onChange={e => {
                          setForm(p => ({ ...p, userdiag_link: e.target.value }));
                          setUserdiagDone(e.target.value.includes('userdiag'));
                        }}
                        placeholder="https://userdiag.com/r/..."
                        className={`w-full bg-zinc-950 border rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 outline-none transition-all pr-10 ${
                          userdiagDone ? 'border-green-500/60 ring-1 ring-green-500/20' : 'border-zinc-700 focus:border-cyan-500'
                        }`}
                        data-testid="userdiag-link-input"
                      />
                      {userdiagDone && (
                        <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-400" />
                      )}
                    </div>
                    {userdiagDone && (
                      <p className="text-green-400 text-xs mt-1 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Lien UserDiag détecté
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-2 text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <button type="submit" disabled={submitting}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white font-semibold rounded-xl transition-colors"
                  data-testid="submit-ticket-btn">
                  {submitting ? <Loader className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
                  {submitting ? 'Envoi...' : 'Envoyer le ticket'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setError(''); }}
                  className="px-4 py-3 bg-zinc-800 hover:bg-zinc-700 text-gray-300 rounded-xl transition-colors text-sm">
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Ticket list */}
        <div>
          <h2 className="text-white font-semibold mb-4 flex items-center gap-2 text-sm uppercase tracking-wide text-gray-400">
            Mes tickets ({myTickets.length})
          </h2>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader className="w-6 h-6 text-cyan-400 animate-spin" />
            </div>
          ) : myTickets.length === 0 ? (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-12 text-center">
              <MessageSquare className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400 text-sm">Aucun ticket pour l'instant</p>
              <button onClick={() => setShowForm(true)}
                className="mt-4 px-5 py-2 bg-cyan-600/80 hover:bg-cyan-600 text-white text-sm rounded-lg transition-colors">
                Ouvrir mon premier ticket
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {myTickets.map(ticket => {
                const status = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
                const cat = catMap[ticket.category];
                const CatIcon = cat?.icon || HelpCircle;
                const isExpanded = expandedTicket === ticket.id;

                return (
                  <div key={ticket.id} data-testid={`ticket-${ticket.id}`}
                    className="bg-zinc-900/50 border border-zinc-800 hover:border-cyan-900/50 rounded-2xl overflow-hidden transition-all">

                    {/* Ticket header — always visible */}
                    <button onClick={() => setExpandedTicket(isExpanded ? null : ticket.id)}
                      className="w-full px-5 py-4 flex items-center gap-4 text-left">
                      <div className="shrink-0 w-9 h-9 rounded-xl bg-zinc-800 flex items-center justify-center">
                        <CatIcon className={`w-4 h-4 ${cat?.color || 'text-gray-400'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-gray-400 text-xs font-mono">#{ticket.ticket_number}</span>
                          <span className="text-white text-sm font-medium truncate">{ticket.subject}</span>
                        </div>
                        <p className="text-gray-500 text-xs mt-0.5">
                          {new Date(ticket.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                      </div>
                      <div className="shrink-0 flex items-center gap-2">
                        <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${status.bg} ${status.color}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`}></span>
                          {status.label}
                        </span>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                      </div>
                    </button>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div className="border-t border-zinc-800 px-5 py-4 space-y-4">
                        <div>
                          <p className="text-xs text-gray-500 font-medium mb-1.5">Description</p>
                          <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{ticket.description}</p>
                        </div>

                        {ticket.admin_note && (
                          <div className="p-3 bg-cyan-500/5 border border-cyan-500/20 rounded-xl">
                            <p className="text-xs text-cyan-400 font-medium mb-1">Réponse du staff</p>
                            <p className="text-gray-200 text-sm">{ticket.admin_note}</p>
                          </div>
                        )}

                        {ticket.userdiag_link && (
                          <div className="flex items-center gap-2 p-2.5 bg-cyan-900/20 border border-cyan-800/30 rounded-xl">
                            <Monitor className="w-4 h-4 text-cyan-400 shrink-0" />
                            <span className="text-xs text-gray-400">UserDiag :</span>
                            <a href={ticket.userdiag_link} target="_blank" rel="noreferrer"
                              className="text-xs text-cyan-400 hover:text-cyan-300 underline truncate flex items-center gap-1">
                              Voir le diagnostic <ExternalLink className="w-3 h-3 shrink-0" />
                            </a>
                          </div>
                        )}

                        {ticket.discord_channel_id && (
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <ExternalLink className="w-3.5 h-3.5" />
                            Canal Discord créé — rejoins le serveur pour voir les messages
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default SupportPage;
